import classNames from "classnames";
import { DateTime } from "luxon";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  OnboardStatus,
  Organization,
  OrganizationStatus,
} from "~@/graphql/codegen/generated";
import {
  ClockFilledIcon,
  ForbiddenIcon,
  SquareCheckFilledIcon,
  WarningFilledIcon,
  WarningRhombusIcon,
} from "~@/icons";

export type OrganizationBadgeKind =
  | "deleting"
  | "stale"
  | "draft"
  | "trial"
  | "trial-ended"
  | "active-payment-failed"
  | "active-canceled"
  | "active"
  | "inactive-canceled"
  | "inactive-payment-failed"
  | "inactive-trial-not-converted"
  | "inactive";

export type OrganizationBadgeCss = "active" | "inactive" | "draft";

export type OrganizationBadgeIcon =
  | "clock"
  | "forbidden"
  | "warning-rhombus"
  | "warning-filled"
  | "check";

export type OrganizationBadgeState = {
  kind: OrganizationBadgeKind;
  labelKey: string;
  labelParams?: { count: number };
  cssModifier: OrganizationBadgeCss;
  icon: OrganizationBadgeIcon;
  showStaleCountdown: boolean;
};

export type OrganizationStatusBadgeInput = Pick<Organization, "status"> &
  Partial<
    Pick<
      Organization,
      | "isCanceled"
      | "isPaymentFailed"
      | "deletedAt"
      | "isPendingDeletion"
      | "isStale"
      | "daysUntilStale"
      | "isTrial"
      | "trialFinishAt"
      | "onboardStatus"
      | "parentOID"
    >
  >;

function isOnboardDone(
  onboardStatus: OnboardStatus | string | null | undefined
): boolean {
  return onboardStatus === OnboardStatus.Done || onboardStatus === "DONE";
}

function trialDaysLeft(trialFinishAt: string, now: Date): number {
  const finish = DateTime.fromISO(trialFinishAt, { zone: "utc" });
  if (!finish.isValid) {
    return -1;
  }
  const current = DateTime.fromJSDate(now, { zone: "utc" });
  return Math.max(0, Math.ceil(finish.diff(current, "days").days));
}

// [AI_START::function id="organizationBadgeState" purpose="Exact company state for the agency badge; Hidden is a tab, not a kind; first match of 13 rules wins" links=["OrganizationStatusBadge", "organizationStatusBadgeProps"]]
export function organizationBadgeState(
  input: OrganizationStatusBadgeInput,
  now: Date = new Date()
): OrganizationBadgeState {
  /** Hidden (deletedAt) is ignored: the tab already tells the operator. */
  if (input.isPendingDeletion) {
    return {
      kind: "deleting",
      labelKey: "organization-status.deleting",
      cssModifier: "inactive",
      icon: "forbidden",
      showStaleCountdown: false,
    };
  }
  if (input.isStale) {
    return {
      kind: "stale",
      labelKey: "organization-status.stale",
      cssModifier: "inactive",
      icon: "forbidden",
      showStaleCountdown: false,
    };
  }
  if (input.status === OrganizationStatus.Draft) {
    return {
      kind: "draft",
      labelKey: "organization-status.in-registration",
      cssModifier: "draft",
      icon: "clock",
      showStaleCountdown: false,
    };
  }
  if (input.parentOID == null) {
    return {
      kind: "active",
      labelKey: "organization-status.active",
      cssModifier: "active",
      icon: "check",
      showStaleCountdown: false,
    };
  }
  if (input.status === OrganizationStatus.Active && Boolean(input.isTrial)) {
    if (!input.trialFinishAt) {
      return {
        kind: "trial",
        labelKey: "organization-status.trial",
        cssModifier: "active",
        icon: "clock",
        showStaleCountdown: false,
      };
    }
    const days = trialDaysLeft(input.trialFinishAt, now);
    if (days < 0) {
      return {
        kind: "trial",
        labelKey: "organization-status.trial",
        cssModifier: "active",
        icon: "clock",
        showStaleCountdown: false,
      };
    }
    if (days === 0) {
      return {
        kind: "trial",
        labelKey: "organization-status.trial-ends-today",
        cssModifier: "active",
        icon: "clock",
        showStaleCountdown: false,
      };
    }
    if (days === 1) {
      return {
        kind: "trial",
        labelKey: "organization-status.trial-one-day-left",
        cssModifier: "active",
        icon: "clock",
        showStaleCountdown: false,
      };
    }
    return {
      kind: "trial",
      labelKey: "organization-status.trial-days-left",
      labelParams: { count: days },
      cssModifier: "active",
      icon: "clock",
      showStaleCountdown: false,
    };
  }
  if (
    input.status === OrganizationStatus.Active &&
    !input.isTrial &&
    !isOnboardDone(input.onboardStatus)
  ) {
    return {
      kind: "trial-ended",
      labelKey: "organization-status.trial-ended",
      cssModifier: "inactive",
      icon: "forbidden",
      showStaleCountdown: false,
    };
  }
  if (input.status === OrganizationStatus.Active && input.isPaymentFailed) {
    return {
      kind: "active-payment-failed",
      labelKey: "organization-status.active-payment-failed",
      cssModifier: "active",
      icon: "warning-rhombus",
      showStaleCountdown: false,
    };
  }
  if (input.status === OrganizationStatus.Active && input.isCanceled) {
    return {
      kind: "active-canceled",
      labelKey: "organization-status.active-canceled",
      cssModifier: "active",
      icon: "warning-filled",
      showStaleCountdown: false,
    };
  }
  if (input.status === OrganizationStatus.Active) {
    return {
      kind: "active",
      labelKey: "organization-status.active",
      cssModifier: "active",
      icon: "check",
      showStaleCountdown: false,
    };
  }
  if (input.status === OrganizationStatus.Inactive && input.isCanceled) {
    return {
      kind: "inactive-canceled",
      labelKey: "organization-status.inactive-canceled",
      cssModifier: "inactive",
      icon: "forbidden",
      showStaleCountdown: true,
    };
  }
  if (input.status === OrganizationStatus.Inactive && input.isPaymentFailed) {
    return {
      kind: "inactive-payment-failed",
      labelKey: "organization-status.inactive-payment-failed",
      cssModifier: "inactive",
      icon: "forbidden",
      showStaleCountdown: true,
    };
  }
  if (
    input.status === OrganizationStatus.Inactive &&
    !isOnboardDone(input.onboardStatus)
  ) {
    return {
      kind: "inactive-trial-not-converted",
      labelKey: "organization-status.inactive-trial-not-converted",
      cssModifier: "inactive",
      icon: "forbidden",
      showStaleCountdown: true,
    };
  }
  return {
    kind: "inactive",
    labelKey: "organization-status.inactive",
    cssModifier: "inactive",
    icon: "forbidden",
    showStaleCountdown: true,
  };
}
// [AI_END::organizationBadgeState]

export function organizationStatusBadgeProps(
  organization: OrganizationStatusBadgeInput
) {
  return {
    status: organization.status,
    isCanceled: !!organization.isCanceled,
    isPaymentFailed: !!organization.isPaymentFailed,
    isPendingDeletion: Boolean(organization.isPendingDeletion),
    isStale: Boolean(organization.isStale),
    daysUntilStale: organization.daysUntilStale ?? null,
    isTrial: Boolean(organization.isTrial),
    trialFinishAt: organization.trialFinishAt ?? null,
    onboardStatus: organization.onboardStatus ?? null,
    parentOID: organization.parentOID ?? null,
  };
}

const BADGE_ICONS: Record<OrganizationBadgeIcon, typeof ClockFilledIcon> = {
  clock: ClockFilledIcon,
  forbidden: ForbiddenIcon,
  "warning-rhombus": WarningRhombusIcon,
  "warning-filled": WarningFilledIcon,
  check: SquareCheckFilledIcon,
};

// [AI_START::function id="OrganizationStatusBadge" purpose="Agency badge shows company state only; Hidden is the list tab; countdown only on Inactive variants" links=["organizationBadgeState"]]
const OrganizationStatusBadge: FC<{
  status: OrganizationStatus;
  isCanceled: boolean;
  isPaymentFailed: boolean;
  isPendingDeletion?: boolean | null;
  isStale?: boolean | null;
  daysUntilStale?: number | null;
  isTrial?: boolean | null;
  trialFinishAt?: string | null;
  onboardStatus?: OnboardStatus | null;
  parentOID?: string | null;
}> = (props) => {
  const { t } = useTranslation();
  const state = useMemo(() => organizationBadgeState(props), [props]);
  const Icon = BADGE_ICONS[state.icon];

  const staleCountdown = useMemo(() => {
    if (!state.showStaleCountdown || props.daysUntilStale == null) {
      return null;
    }
    if (props.daysUntilStale <= 0) {
      return t("organization-status.stale-today");
    }
    if (props.daysUntilStale === 1) {
      return t("organization-status.stale-in-one-day");
    }
    return t("organization-status.stale-in-days", {
      count: props.daysUntilStale,
    });
  }, [props.daysUntilStale, state.showStaleCountdown, t]);

  return (
    <div
      className={classNames("organization-status", {
        "organization-status--is-active": state.cssModifier === "active",
        "organization-status--is-inactive": state.cssModifier === "inactive",
        "organization-status--is-draft": state.cssModifier === "draft",
      })}
    >
      <div className="icon-text">
        <span className="icon icon--is-14x14">
          <Icon />
        </span>
        <span>
          {state.labelParams
            ? t(state.labelKey, { count: state.labelParams.count })
            : t(state.labelKey)}
        </span>
      </div>
      {staleCountdown ? (
        <span className="organization-status__countdown">{staleCountdown}</span>
      ) : null}
    </div>
  );
};
// [AI_END::OrganizationStatusBadge]

export default OrganizationStatusBadge;
